import React, { useState, useMemo, useEffect } from 'react';
import { useCustomers } from '../hooks/useCustomers';
import { useOrganizations } from '../hooks/useOrganizations';
import CustomerForm from '../components/customers/CustomerForm';
import { Customer, CreateCustomerData } from '../types/customer';
import { Organization } from '../types/organization';
import {
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOffice2Icon,
  UserIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  DocumentIcon,
  DocumentTextIcon,
  PaperClipIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  EyeIcon,
  UsersIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '../components/common/Tooltip';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/common/Modal';
import { formatDocumentDate } from 'utils/formatters';
import Pagination from 'components/common/Pagination';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';
import CustomerViewModal from 'components/customers/CustomerViewModal';
import CustomerList from 'components/customers/CustomerList';
import { customerToKeyValuePairs } from '../utils/customerDataMapper';
import DocumentViewerModal from 'components/documents/DocumentViewerModal';
import DocumentGeneratorModal from 'components/documents/DocumentGeneratorModal';
import CommonPageHeader from '../components/common/CommonPageHeader';
import CustomerRegistrationLinkGenerator from '../components/customers/CustomerRegistrationLinkGenerator';
import GroupAssignmentModal from '../components/customers/GroupAssignmentModal';
import SendInvitationModal from '../components/customers/SendInvitationModal';

export interface SimpleOrganization {
  id: string;
  name: string;
}

const Customers: React.FC = () => {
  const { t } = useLanguage();
  const { customers, loading, createCustomer, updateCustomer, deleteCustomer, bulkDeleteCustomers, refresh } = useCustomers();
  const { organizations } = useOrganizations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isDocumentGeneratorOpen, setIsDocumentGeneratorOpen] = useState(false);
  const [customerWithDocuments, setCustomerWithDocuments] = useState<Customer | null>(null);
  const [customerForDocGen, setCustomerForDocGen] = useState<Customer | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isRegistrationLinkOpen, setIsRegistrationLinkOpen] = useState(false);
  const [isSendInvitationOpen, setIsSendInvitationOpen] = useState(false);
  const [customerForInvitation, setCustomerForInvitation] = useState<Customer | null>(null);

  // FAB trigger handling
  useEffect(() => {
    const handleOpenCustomerForm = () => {
      setSelectedCustomer(null);
      setIsModalOpen(true);
    };

    window.addEventListener('openCustomerForm', handleOpenCustomerForm);
    return () => {
      window.removeEventListener('openCustomerForm', handleOpenCustomerForm);
    };
  }, []);

  // Bulk select state
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [showGroupAssignment, setShowGroupAssignment] = useState(false);
  const [isConfirmBulkDeleteOpen, setIsConfirmBulkDeleteOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleCreateCustomer = async (data: CreateCustomerData) => {
    try {
      await createCustomer(data);
      setIsModalOpen(false);
      toast.success(t('common.messages.customer.createdSuccess'));
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(t('common.messages.customer.createdError'));
    }
  };

  const handleEditCustomer = async (data: CreateCustomerData) => {
    if (!selectedCustomer) return;

    try {
      await updateCustomer(selectedCustomer.id, data);
      setIsModalOpen(false);
      setSelectedCustomer(null);
      toast.success(t('common.messages.customer.updatedSuccess'));
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(t('common.messages.customer.updatedError'));
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    try {
      await deleteCustomer(customer.id);
      toast.success(t('common.messages.customer.deletedSuccess'));
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('common.messages.customer.deletedError'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) return;

    try {
      const result = await bulkDeleteCustomers(selectedCustomerIds);
      
      if (result.failed === 0) {
        toast.success(t('customers.bulkActions.deleteSuccess', { count: result.success }));
      } else if (result.success > 0) {
        toast.success(t('customers.bulkActions.deletePartialSuccess', { 
          success: result.success, 
          failed: result.failed 
        }));
      } else {
        toast.error(t('customers.bulkActions.deleteFailed'));
      }
      
      setSelectedCustomerIds([]);
      setIsConfirmBulkDeleteOpen(false);
    } catch (error) {
      console.error('Error bulk deleting customers:', error);
      toast.error(t('customers.bulkActions.deleteFailed'));
    }
  };

  const handleStatusChange = async (customer: Customer) => {
    try {
      const newStatus = customer.status === 'active' ? 'inactive' : 'active';
      await updateCustomer(customer.id, {
        ...customer,
        status: newStatus,
        isActive: newStatus === 'active'
      });
      toast.success(`${t('common.messages.customer.customerStatusUpdated')} ${customer.status === 'active' ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error updating customer status:', error);
      toast.error(t('common.messages.customer.customerStatusUpdateFailed'));
    }
  };

  const openEditModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  // Function to open the document viewer modal
  const openDocumentModal = (customer: Customer) => {
    if (customer.documents && customer.documents.length > 0) {
      setCustomerWithDocuments(customer);
      setIsDocumentModalOpen(true);
    } else {
      toast.error(t('customers.messages.noDocuments') || 'No documents available');
    }
  };

  const openViewModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsViewModalOpen(true);
  };

  const handleGenerateDocument = (customer: Customer) => {
    setCustomerForDocGen(customer);
    setIsDocumentGeneratorOpen(true);
  };

  const handleSendInvitation = async (customer: Customer) => {
    // Refresh customer data to get latest contactPersons
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../lib/firebase');
      
      const customerDoc = await getDoc(doc(db, 'customers', customer.id));
      if (customerDoc.exists()) {
        const freshCustomer = {
          id: customerDoc.id,
          ...customerDoc.data(),
          createdAt: customerDoc.data().createdAt?.toDate(),
          updatedAt: customerDoc.data().updatedAt?.toDate()
        } as Customer;
        
        setCustomerForInvitation(freshCustomer);
      } else {
        setCustomerForInvitation(customer);
      }
    } catch (error) {
      console.error('Error refreshing customer:', error);
      setCustomerForInvitation(customer);
    }
    
    setIsSendInvitationOpen(true);
  };

  // Szűrés kezelése
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Név vagy email alapján szűrés
      const searchMatch =
        (customer.type === 'organization' ? customer.companyName : `${customer.lastName} ${customer.firstName}`)
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Adószám és cégjegyzékszám keresés
        customer.taxNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Cím elemek keresése
        customer.address?.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.postalCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.country?.toLowerCase().includes(searchTerm.toLowerCase());

      // Szervezet alapján szűrés
      const orgMatch = selectedOrgs.length === 0 ||
        customer.organizations?.some(org => selectedOrgs.includes(org));

      return searchMatch && orgMatch;
    });
  }, [customers, searchTerm, selectedOrgs]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const exportToCSV = () => {
    // CSV fejléc
    const headers = [
      'Type',
      'Company Name',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Tax Number',
      'Organizations',
      'Status',
      'Notes',
      'Address',
      'Contact Person'
    ].join(',');

    // CSV sorok generálása
    const csvRows = filteredCustomers.map(customer => {
      const address = `${customer.address.street}, ${customer.address.city}, ${customer.address.postalCode}, ${customer.address.country}`;
      const contactPerson = `${customer.contactPerson.name} (${customer.contactPerson.email}, ${customer.contactPerson.phone}, ${customer.contactPerson.position})`;

      // A szervezetek nevének összegyűjtése
      const orgNames = customer.organizations
        ?.map(orgId => {
          const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
          return org?.name;
        })
        .filter((name): name is string => Boolean(name))
        .join('; ') || '';

      return [
        customer.type,
        customer.companyName || '',
        customer.firstName || '',
        customer.lastName || '',
        customer.email,
        customer.phone || '',
        customer.taxNumber || '',
        orgNames,
        customer.status,
        customer.notes || '',
        address,
        contactPerson
      ].map(field => `"${field}"`).join(',');
    });

    // CSV tartalom összeállítása
    const csvContent = [headers, ...csvRows].join('\n');

    // Fájl létrehozása és letöltése
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `customers_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleAddOrganization = async (customer: Customer, orgId: string) => {
    try {
      const currentOrgs = customer.organizations || [];
      const newOrgs = [...currentOrgs, orgId];

      await updateCustomer(customer.id, {
        ...customer,
        organizations: newOrgs
      });
      toast.success(t('customers.messages.organizationsUpdated'));
    } catch (error) {
      console.error('Error adding organization:', error);
      toast.error(t('customers.messages.organizationsUpdateFailed'));
    }
  };

  const handleRemoveOrganization = async (customer: Customer, orgId: string) => {
    try {
      const currentOrgs = customer.organizations || [];
      const newOrgs = currentOrgs.filter(id => id !== orgId);

      await updateCustomer(customer.id, {
        ...customer,
        organizations: newOrgs
      });
      toast.success(t('customers.messages.organizationsUpdated'));
    } catch (error) {
      console.error('Error removing organization:', error);
      toast.error(t('customers.messages.organizationsUpdateFailed'));
    }
  };

  // Bulk select handlers
  const handleSelectCustomer = (customerId: string) => {
    setSelectedCustomerIds(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomerIds.length === currentCustomers.length) {
      setSelectedCustomerIds([]);
    } else {
      setSelectedCustomerIds(currentCustomers.map(c => c.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedCustomerIds([]);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 max-w-[100vw] px-2 sm:px-0 overflow-x-hidden">
      <CommonPageHeader
        title={t('customers.title')}
        subtitle={t('customers.subtitle') || 'Ügyfelek kezelése és áttekintése'}
        icon={UsersIcon}
      >
        <div className="flex space-x-2">
          <button
            onClick={() => setIsRegistrationLinkOpen(true)}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            <span>Regisztrációs link</span>
          </button>
          <button
            onClick={() => { setSelectedCustomer(null); setIsModalOpen(true); }}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            <span>{t('customers.new')}</span>
          </button>
        </div>
      </CommonPageHeader>

      {/* Szűrési lehetőségek - görgethetők mobilon */}
      <div className="space-y-2 sm:space-y-3">
        <div className="w-full">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.placeholders.selectCustomer')}
          />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
          <button
            onClick={() => setSelectedOrgs([])}
            className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
              ${selectedOrgs.length === 0
                ? 'border-primary-500 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
          >
            <BuildingOffice2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            {t('customers.fields.organizations')}
          </button>
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => {
                setSelectedOrgs(prev =>
                  prev.includes(org.id)
                    ? prev.filter(id => id !== org.id)
                    : [...prev, org.id]
                );
              }}
              className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
                ${selectedOrgs.includes(org.id)
                  ? 'border-primary-500 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <BuildingOffice2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="max-w-[120px] sm:max-w-[150px] truncate">{org.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {/* Bulk Actions Toolbar */}
      {selectedCustomerIds.length > 0 && (
        <div className="mb-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                {t('customers.bulkActions.selected', { count: selectedCustomerIds.length })}
              </span>
              <button
                onClick={() => setSelectedCustomerIds([])}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                {t('customers.bulkActions.deselectAll')}
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowGroupAssignment(true)}
                className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
              >
                <UsersIcon className="h-4 w-4 mr-2" />
                Hozzáadás csoporthoz
              </button>
              <button
                onClick={() => setIsConfirmBulkDeleteOpen(true)}
                className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {t('customers.bulkActions.deleteSelected')}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerList
        customers={currentCustomers}
        organizations={organizations}
        onStatusChange={handleStatusChange}
        onViewClick={openViewModal}
        onEditClick={openEditModal}
        onDeleteClick={(customer) => {
          setCustomerToDelete(customer);
          setIsConfirmDeleteOpen(true);
        }}
        onDocumentClick={openDocumentModal}
        onGenerateDocument={handleGenerateDocument}
        onAddOrganization={handleAddOrganization}
        onRemoveOrganization={handleRemoveOrganization}
        onSendInvitation={handleSendInvitation}
        selectedCustomerIds={selectedCustomerIds}
        onSelectCustomer={handleSelectCustomer}
        onSelectAll={handleSelectAll}
        showBulkSelect={true}
      />

      {/* Pagination controls */}
      <div className="relative z-10">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredCustomers.length}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
        />
      </div>

      <CustomerViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        customer={selectedCustomer}
        organizations={organizations}
        onEdit={openEditModal}
      />

      <CustomerForm
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSubmit={selectedCustomer ? handleEditCustomer : handleCreateCustomer}
        initialData={selectedCustomer}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        customerWithDocuments={customerWithDocuments}
      />

      {/* Dokumentum generáló modal */}
      <DocumentGeneratorModal
        isOpen={isDocumentGeneratorOpen}
        onClose={() => setIsDocumentGeneratorOpen(false)}
        documentDataProvider={customerForDocGen}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={() => {
          if (customerToDelete) {
            handleDeleteCustomer(customerToDelete);
          }
          setIsConfirmDeleteOpen(false);
          setCustomerToDelete(null);
        }}
        title={t('customers.messages.confirmTitle')}
        deleteMessage={customerToDelete ?
          `${t('customers.messages.deleteQuestion')} "${customerToDelete.type === 'organization' ? customerToDelete.companyName : `${customerToDelete.firstName} ${customerToDelete.lastName}`}"?` :
          t('customers.messages.deleteQuestion')}
        deleteWarning={t('customers.messages.deleteWarning')}
      />

      {/* Confirm Bulk Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmBulkDeleteOpen}
        onClose={() => setIsConfirmBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title={t('customers.bulkActions.confirmDelete', { count: selectedCustomerIds.length })}
        deleteMessage={t('customers.bulkActions.confirmDeleteMessage')}
        deleteWarning={t('customers.messages.deleteWarning')}
      />

      {/* Customer Registration Link Generator Modal */}
      <CustomerRegistrationLinkGenerator
        isOpen={isRegistrationLinkOpen}
        onClose={() => setIsRegistrationLinkOpen(false)}
      />

      {/* Group Assignment Modal */}
      <GroupAssignmentModal
        isOpen={showGroupAssignment}
        onClose={() => setShowGroupAssignment(false)}
        customerIds={selectedCustomerIds}
        onSuccess={() => {
          setSelectedCustomerIds([]);
          setShowGroupAssignment(false);
        }}
      />

      {/* Send Invitation Modal */}
      {customerForInvitation && (
        <SendInvitationModal
          isOpen={isSendInvitationOpen}
          onClose={() => {
            setIsSendInvitationOpen(false);
            setCustomerForInvitation(null);
          }}
          customer={customerForInvitation}
          onSuccess={() => {
            // Refresh customer list to get updated contact persons
            refresh();
          }}
        />
      )}

    </div>
  );
};

export default Customers;